import { imgProxy } from "@/lib/img";
import { ExternalLink, Copy, MessageSquare, History } from "lucide-react";
import FavoriteButton from "@/components/dashboard/FavoriteButton";
import WhatsAppButton from "@/components/dashboard/WhatsAppButton";
import { FeaturedBadge } from "@/components/dashboard/ProfileCard";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtPhone(p) {
  if (!p) return null;
  return p.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
}

// One row of the profiles table. Purely presentational: all interactions are
// delegated to the parent through callbacks so the table stays the single owner
// of state (selection, dialogs, phone-group search).
export default function ProfileRow({
  p, i, isSelected, sourceColor, placeholder,
  onToggleSelect, onOpenDetail, onPhoneGroup, onNotes, onTimeline,
}) {
  return (
    <tr data-testid={`profile-row-${i}`} className={`border-t border-[#27272a] hover:bg-[#27272a]/40 transition-colors ${isSelected ? "bg-[#c8aa6e]/5" : ""}`}>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          data-testid={`select-${i}`}
          checked={isSelected}
          onChange={() => onToggleSelect(p.id)}
          className="accent-[#c8aa6e] w-4 h-4 cursor-pointer"
        />
      </td>
      <td className="px-4 py-2">
        <button
          type="button"
          data-testid={`photo-btn-${i}`}
          onClick={() => onOpenDetail(p.id)}
          title="Ver foto e dados do perfil"
          className="relative block w-14 h-14"
        >
          <img
            src={imgProxy(p.photo_url, 160) || placeholder}
            onError={(e) => (e.currentTarget.src = placeholder)}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-14 h-14 object-cover grayscale-[20%] hover:grayscale-0 hover:ring-2 hover:ring-[#c8aa6e] transition-all cursor-pointer bg-[#27272a]"
          />
          {p.featured_tier && (
            <span className="absolute -top-1.5 -left-1.5">
              <FeaturedBadge tier={p.featured_tier} compact testid={`row-featured-${i}`} />
            </span>
          )}
        </button>
      </td>
      <td className="px-4 py-2 text-[#fafafa] font-medium">
        <div className="flex items-center gap-2">
          <FavoriteButton profileId={p.id} initial={p.is_favorite} size={15} />
          <span className={p.status === "inativo" ? "text-[#a1a1aa]" : ""}>{p.name || "—"}</span>
          {p.status === "inativo" && (
            <span className="text-[9px] uppercase tracking-wide text-[#ef4444] border border-[#ef4444]/40 px-1 py-0.5" data-testid={`row-inactive-${i}`}>
              Desativado
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-[#a1a1aa]">{p.city || "—"}</td>
      <td className="px-4 py-2 text-[#a1a1aa]">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: sourceColor }} />
          {p.source_name}
        </span>
      </td>
      <td className="px-4 py-2">
        {p.phone ? (
          <span className="inline-flex items-center gap-2 font-mono text-[#fafafa]">
            {fmtPhone(p.phone)}
            <WhatsAppButton phone={p.phone} name={p.name} size={15} />
            {p.phone_ad_count > 1 && (
              <button
                type="button"
                data-testid={`phone-group-${i}`}
                onClick={() => onPhoneGroup(p.phone)}
                title={`Ver os ${p.phone_ad_count} anúncios com este número`}
                className="inline-flex items-center gap-1 text-[10px] text-[#c8aa6e] border border-[#c8aa6e]/40 px-1.5 py-0.5 hover:bg-[#c8aa6e]/15 hover:border-[#c8aa6e] transition-colors cursor-pointer"
              >
                <Copy className="w-3 h-3" /> {p.phone_ad_count}
              </button>
            )}
          </span>
        ) : (
          <span className="text-[#52525b]">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-[#a1a1aa] whitespace-nowrap">{fmtDate(p.first_seen)}</td>
      <td className="px-4 py-2 text-[#a1a1aa] whitespace-nowrap">{fmtDate(p.last_seen)}</td>
      <td className="px-4 py-2">
        <button
          data-testid={`comments-btn-${i}`}
          onClick={() => onNotes(p)}
          className="inline-flex items-center gap-1.5 text-sm text-[#a1a1aa] hover:text-[#c8aa6e] transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          {p.comment_count > 0 && <span className="font-mono text-xs">{p.comment_count}</span>}
        </button>
      </td>
      <td className="px-4 py-2">
        <button
          data-testid={`timeline-btn-${i}`}
          onClick={() => onTimeline(p)}
          title="Ver histórico / linha do tempo"
          className="inline-flex items-center gap-1.5 text-sm text-[#a1a1aa] hover:text-[#c8aa6e] transition-colors"
        >
          <History className="w-4 h-4" />
        </button>
      </td>
      <td className="px-4 py-2">
        <a href={p.profile_url} target="_blank" rel="noreferrer" className="text-[#c8aa6e] hover:text-[#d4b982]">
          <ExternalLink className="w-4 h-4" />
        </a>
      </td>
    </tr>
  );
}
